/// <amd-module name="Wiki/Editor/Components/WikiEditor" />
import * as React from "react";

import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { css, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { WikiPage } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { addAttachments } from "Wiki/Editor/AttachmentHelpers";
import { EditorTextArea } from "Wiki/Editor/Components/EditorTextArea";
import { WikiEditorHeader, WikiEditorViewMode } from "Wiki/Editor/Components/WikiEditorHeader";
import { IEditorActionCreator, IWikiEditorState } from "Wiki/Editor/Contracts";
import { showCloseDialog } from "Wiki/Editor/EditorDialogsHandler";
import { publishPageSaveAttempted } from "Wiki/Editor/EditTelemetryHelper";
import { isDirty } from "Wiki/Editor/Helpers";
import * as Resources from "Wiki/Editor/Resources";
import { ScrollHelper } from "Wiki/Editor/ScrollHelper";
import { PreviewMode, WikiMarkdownRenderer } from "Wiki/Renderer/WikiMarkdownRenderer";
import { PathConstants } from "Wiki/ViewCommon/Constants";
import { OperationStatus, WikiInfo, WikiPageInfoWithName } from "Wiki/ViewCommon/Contracts";
import { Areas, PageErrorType, PerformanceConstants, SaveOptionType, TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getWikiTypeString, isEdge, isIncludeEditingAllowed, publish } from "Wiki/ViewCommon/Utils/WikiHelper";

export interface IWikiEditorProps {
    editorState: IWikiEditorState;
    actionCreator: IEditorActionCreator;
    wikiInfo: WikiInfo;
    pageInfo: WikiPageInfoWithName;
    wikiPages: WikiPage[];
    getPagesForFiltering: () => Promise<WikiPage[]>;
    anchor?: string;
    onAnchorClick?: (anchor: string) => void;
    getWikiPage?: (path: string) => WikiPage | undefined;
    isHandleBrokenLinksEnabled: boolean;
}

interface IWikiEditorViewState {
    viewMode: WikiEditorViewMode;
    synchronizeScroll?: boolean;
}

const wikiEditorMdRendererClassName = "editor-md-renderer";
const synchronizeScrollSettingsKey = "Wiki/Editor/synchronizeScroll";

export class WikiEditor extends VssComponent<IWikiEditorProps, IWikiEditorViewState> {
    private _ref = React.createRef<HTMLDivElement>();
    private _textAreaRef = React.createRef<HTMLTextAreaElement>();
    private _markdownContainerRef = React.createRef<HTMLDivElement>();

    private _wikiToolbarRef = React.createRef<any>();
    private _shouldFocusEditorTextOnMount = false;

    private _scrollHelper: ScrollHelper | undefined;
    private _settingsService: ISettingsService | undefined;

    constructor(props: IWikiEditorProps, context: IVssComponentContext) {
        super(props, context);

        if (props.pageInfo.pageName) {
            this._shouldFocusEditorTextOnMount = true;
        }

        let viewMode = WikiEditorViewMode.EditAndPreview;
        if (isFeatureFlagEnabled(this.context.pageContext, "Wiki.SyncScroll")) {
            this._scrollHelper = new ScrollHelper();
            viewMode = WikiEditorViewMode.EditAndPreview;
            this._settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        }
        this.state = { viewMode, synchronizeScroll: false };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        const textAreaElement = this._textAreaRef.current;
        if (this._shouldFocusEditorTextOnMount && textAreaElement) {
            if (isEdge()) {
                textAreaElement.focus();
            }
            textAreaElement.selectionStart = 0;
            textAreaElement.selectionEnd = 0;
            textAreaElement.focus();
        }

        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.endScenario(Areas.Wiki, PerformanceConstants.Editor, false);

        document.body.addEventListener("fpsStarting", this._onFpsStarting);
        window.addEventListener("beforeunload", this._onBeforeUnload);

        if (this._scrollHelper && this._settingsService && this._textAreaRef.current && this._markdownContainerRef.current) {
            let isSyncScrollEnabled: boolean | undefined = this._settingsService.getEntry(synchronizeScrollSettingsKey, SettingsUserScope.Me);
            isSyncScrollEnabled = isSyncScrollEnabled === undefined ? true : isSyncScrollEnabled;
            this.setState({ synchronizeScroll: isSyncScrollEnabled });

            if (isSyncScrollEnabled) {
                this._scrollHelper.initialize(this._textAreaRef.current, this._markdownContainerRef.current);
            }
        }

        this._registerShortcuts();
    }

    public componentDidUpdate(prevProps: IWikiEditorProps, prevState: IWikiEditorViewState): void {
        super.componentDidUpdate(prevProps, prevState);

        if (this.state.synchronizeScroll && prevState.viewMode !== this.state.viewMode && this._scrollHelper) {
            if (this.state.viewMode === WikiEditorViewMode.EditAndPreview && this._markdownContainerRef.current && this._textAreaRef.current) {
                this._scrollHelper.setSyncScroll(true, this._textAreaRef.current, this._markdownContainerRef.current);
            } else {
                this._scrollHelper.setSyncScroll(false);
            }
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        window.removeEventListener("beforeunload", this._onBeforeUnload);
        document.body.removeEventListener("fpsStarting", this._onFpsStarting);

        const shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        shortcutService.removeShortcutGroup(Resources.ShortcutGroup);
    }

    public render(): JSX.Element {
        const { pageInfo, wikiInfo, editorState, anchor, onAnchorClick, getWikiPage } = this.props;
        const { viewMode } = this.state;
        const { repository } = wikiInfo.wikiRepositoryData;
        const editorTextAreaClassName = css("we-ta-container", viewMode === WikiEditorViewMode.PreviewOnly && "hidden");
        const errorMessage = !wikiInfo.wikiPermissions.hasWikiContributePermission
            ? Resources.NoEditPermissionErrorMessage
            : editorState.errorState && editorState.errorState.message;

        const errorButtonProps = editorState.errorState && editorState.errorState.buttonProps;
        return (
            <div className="wiki-editor flex-column" ref={this._ref}>
                <div className={css("save-in-progress", editorState.savePageStatus === OperationStatus.InProgress && "visible")} />
                {errorMessage && (
                    <MessageCard severity={MessageCardSeverity.Error} onDismiss={this._onDismissError} buttonProps={errorButtonProps}>
                        {errorMessage}
                    </MessageCard>
                )}
                <WikiEditorHeader
                    shouldFocusTitleOnMount={!this._shouldFocusEditorTextOnMount}
                    pageName={editorState.pageName}
                    pageNameError={editorState.pageNameError}
                    isDirty={this._isDirty()}
                    viewMode={viewMode}
                    onViewModeChanged={this._onViewModeChanged}
                    onCloseEditor={this._onCloseEditor}
                    onSavePage={this._onSavePage}
                    onUpdatePageName={this._onUpdatePageName}
                    hideCloseButton={editorState.hideCloseButton}
                    isEditDisabled={!wikiInfo.wikiPermissions.hasWikiContributePermission}
                />
                {viewMode != WikiEditorViewMode.PreviewOnly && (
                    // Not passing the modules prop because we are loading the modules in actioncreator hub
                    <WrappedComponent
                        className="we-toolbar"
                        wrappedType={"TFS.Wiki.WikiToolBar"}
                        validAttachmentTypes={PathConstants.AllowedAttachmentFileTypes(this.context.pageContext)}
                        getTextArea={this._getTextArea}
                        wikiType={getWikiTypeString(wikiInfo.wiki.type)}
                        newWiki={true}
                        showLearningBubble
                        onBrowseAttachments={this._onAddAttachments}
                        textChangedByToolbar={this._textChangedByToolbar}
                        disableInsertTextCommand={false}
                        isIncludeEditingAllowed={isIncludeEditingAllowed(this.context.pageContext)}
                        showMermaidCommand={isFeatureFlagEnabled(this.context.pageContext, "Wiki.Web.EnableMermaid", false)}
                        showSyncScrollbutton={isFeatureFlagEnabled(this.context.pageContext, "Wiki.SyncScroll")}
                        isSyncScrollOn={this.state.synchronizeScroll}
                        disableSyncScrollButton={this.state.viewMode === WikiEditorViewMode.EditOnly}
                        onToggleSyncScroll={this._onToogleSyncScroll}
                        pageContext={this.context.pageContext}
                        wrappedRef={this._wikiToolbarRef}
                        onPasteAsHtmlToggle={this.props.actionCreator.togglePasteAsHtmlFlag}
                    />
                )}
                <div className={css("we-text-preview-container flex-row flex-grow", viewMode)}>
                    <EditorTextArea
                        {...this.props}
                        className={editorTextAreaClassName}
                        wikiMentionNwpModule={this.props.editorState.legacyScripts.wikiAutoCompleteNwpWrapperModule}
                        wikiType={this.props.wikiInfo.wiki.type}
                        textAreRef={this._textAreaRef}
                        onError={this._handleErrorMessage}
                        pageContext={this.context.pageContext}
                        onKeyDown={this._onKeyDown}
                    />
                    <div className={css("markdown-preview relative", viewMode === WikiEditorViewMode.EditOnly && "hidden")}>
                        {viewMode !== WikiEditorViewMode.EditOnly && (
                            <WikiMarkdownRenderer
                                className={css("absolute-fill scroll-auto", wikiEditorMdRendererClassName)}
                                wiki={wikiInfo.wiki}
                                pagePath={pageInfo.pageName ? combineUrlPaths(pageInfo.parentPath, pageInfo.pageName) : undefined}
                                wikiPages={this.props.wikiPages}
                                wikiVersion={wikiInfo.wikiVersion}
                                projectId={repository.project.id}
                                repositoryId={repository.id}
                                content={editorState.previewContent || ""}
                                viewMode={viewMode === WikiEditorViewMode.PreviewOnly ? PreviewMode.Off : PreviewMode.On}
                                isSyncScrollEnabled={isFeatureFlagEnabled(this.context.pageContext, "Wiki.SyncScroll")}
                                unsavedAttachmentsMap={this.props.editorState.attachments}
                                anchor={anchor}
                                onAnchorClick={onAnchorClick}
                                scrollToElementClassName={wikiEditorMdRendererClassName}
                                getWikiPage={getWikiPage}
                                onMarkdownLinesMappingChanged={this._scrollHelper ? this._scrollHelper.onMarkdownLinesMappingChanged : undefined}
                                markdownContainerRef={this._markdownContainerRef}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private _onUpdatePageName = (pageName: string): void => {
        this.props.actionCreator.updatePageName(pageName, this.props.pageInfo.parentPath);
    };

    private _onDismissError = (): void => {
        this.props.actionCreator.updateErrorMessage();
    };

    private _onSavePage = (withMessage: boolean, saveOptionType: SaveOptionType, closeEditor: boolean = false): void => {
        const { editorState, pageInfo, wikiInfo, isHandleBrokenLinksEnabled, actionCreator } = this.props;

        publishPageSaveAttempted(this.context.pageContext, editorState, pageInfo, saveOptionType, this.props.wikiInfo.wiki.type);

        actionCreator.savePage(wikiInfo, pageInfo, withMessage, isHandleBrokenLinksEnabled, closeEditor);
    };

    private _onViewModeChanged = (viewMode: WikiEditorViewMode): void => {
        publish(this.context.pageContext, TelemetryConstants.EditContainerPreviewToggled, this.props.wikiInfo.wiki.type, { viewMode }); // Todo check if preview = on is needed
        this.setState({ viewMode });
    };

    private _onToogleSyncScroll = (): void => {
        if (!this._scrollHelper || !this._settingsService || !this._textAreaRef.current || !this._markdownContainerRef.current) {
            return;
        }

        if (this.state.synchronizeScroll) {
            this._scrollHelper.setSyncScroll(false);
        } else {
            this._scrollHelper.setSyncScroll(true, this._textAreaRef.current, this._markdownContainerRef.current);
        }

        this._settingsService.setEntries({ [synchronizeScrollSettingsKey]: !this.state.synchronizeScroll }, SettingsUserScope.Me);

        this.setState({ synchronizeScroll: !this.state.synchronizeScroll });
    };

    private _getTextArea = (): HTMLTextAreaElement | null => {
        return this._textAreaRef.current!;
    };

    private _onAddAttachments = (files: File[]): void => {
        const textArea = this._getTextArea();
        if (textArea) {
            textArea.focus();

            addAttachments(
                this.context.pageContext,
                files,
                this.props.actionCreator.addAttachments,
                textArea.selectionStart,
                textArea.selectionEnd,
                this._handleErrorMessage
            );
        }
    };

    private _textChangedByToolbar = (newText: string, selectionStart: number, selectionEnd: number): void => {
        if (newText) {
            this.props.actionCreator.updatePageContent(newText, selectionStart, selectionEnd);
        } else {
            const textAreaElement = this._getTextArea();
            // To handle Edge bugs and lack of support for insertText in Firefox, we will have to update state as well as component.
            if (textAreaElement) {
                textAreaElement.selectionStart = selectionStart;
                textAreaElement.selectionEnd = selectionEnd;
            }
            this.props.actionCreator.updatePageSelection(selectionStart, selectionEnd);
        }
    };

    private _handleErrorMessage = (message: string, errorType: PageErrorType): void => {
        this.props.actionCreator.handleEditContentError(message, errorType);
    };

    private _onCloseEditor = (): void => {
        this.props.actionCreator.closeEditor(this.props.pageInfo);
    };

    private _isDirty = (): boolean => {
        const { pageInfo, editorState } = this.props;
        return isDirty(pageInfo, editorState);
    };

    private _onBeforeUnload = (ev: BeforeUnloadEvent): string | undefined => {
        if (this._isDirty()) {
            ev.returnValue = Resources.UnsavedChanges_DialogTitle;
            return ev.returnValue;
        }

        return undefined;
    };

    private _onFpsStarting = (ev: CustomEvent): void => {
        if (this._isDirty()) {
            ev.preventDefault();
            ev.detail.cancellationReason = Resources.UnsavedChanges_DialogTitle;
            showCloseDialog(this.context.pageContext, () => {
                document.body.removeEventListener("fpsStarting", this._onFpsStarting);
                FastPageSwitch(ev.detail.pageContext, ev.detail.href, ev.detail.recordHistoryEntry, ev.detail.data);
            });
        }
    };

    private _registerShortcuts = (): void => {
        const shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        if (this.props.wikiInfo.wikiPermissions.hasWikiContributePermission) {
            shortcutService.registerShortcut(Resources.ShortcutGroup, {
                element: this._ref.current!,
                combos: ["mod+s"],
                description: Resources.ShortcutSaveChanges,
                action: () => this._onSavePage(false, SaveOptionType.QuickSaveByShortcut)
            });
            shortcutService.registerShortcut(Resources.ShortcutGroup, {
                element: this._ref.current!,
                combos: ["mod+enter"],
                description: Resources.ShortcutSaveAndClose,
                action: () => this._onSavePage(false, SaveOptionType.QuickSaveByShortcut, true)
            });
        }

        shortcutService.registerShortcut(Resources.ShortcutGroup, {
            element: this._ref.current!,
            combos: ["esc"],
            description: Resources.ShortcutClose,
            action: this._onCloseEditor
        });
    };

    private _onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
        const refObject = this._wikiToolbarRef.current;
        if (refObject && refObject.handleKeyPress) {
            refObject.handleKeyPress(event);
        }
    };
}

VssComponent.register("wiki-editor", WikiEditor);
